import { useMemo, useState, type ReactNode } from 'react';

export interface DataTableColumn<T> {
  key: string;
  he: string;
  en?: string;
  /** תוכן עשיר לכותרת (למשל checkbox בחר-הכל) — עוקף את he/en בתצוגה. */
  heNode?: ReactNode;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'start' | 'center' | 'end';
  width?: string | number;
}

export interface DataTableServerPagination {
  /** עמוד נוכחי, 1-based (כמו Laravel paginator). */
  page: number;
  lastPage: number;
  total: number;
  onPageChange: (page: number) => void;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  lang?: 'he' | 'en';
  pageSize?: number;
  /**
   * עימוד צד-שרת: rows הם עמוד יחיד שכבר נשלף; הדפדוף קורא ל-onPageChange
   * במקום לפרוס את rows לעמודים מקומיים. המיון נשאר בתוך העמוד הנוכחי.
   */
  server?: DataTableServerPagination;
  selectable?: boolean;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  /** Rich empty content (e.g. an EmptyState with a first-run CTA). Overrides emptyMessage. */
  emptyState?: ReactNode;
  loading?: boolean;
  /** Accessible name for the table (screen readers). */
  caption?: string;
  /** שורות דחוסות — ריווח אנכי מוקטן. */
  dense?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function DataTable<T extends Record<string, any>>({
  columns, rows, lang = 'he', pageSize = 25,
  selectable, onRowClick, emptyMessage, emptyState, loading, caption, dense, server,
}: DataTableProps<T>) {
  const rowPad = dense ? '5px' : 'var(--pad-row)';
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const sortedRows = useMemo(() => {
    if (!sort) return rows;

    const sorted = [...rows].sort((a, b) => {
      const av = (a as Record<string, unknown>)[sort.key];
      const bv = (b as Record<string, unknown>)[sort.key];
      // מספרים (או מחרוזות מספריות) ממוינים מספרית — אחרת 100 יוצא לפני 20.
      const an = typeof av === 'number' ? av : av != null && av !== '' && !isNaN(Number(av)) ? Number(av) : NaN;
      const bn = typeof bv === 'number' ? bv : bv != null && bv !== '' && !isNaN(Number(bv)) ? Number(bv) : NaN;
      const cmp = !isNaN(an) && !isNaN(bn)
        ? an - bn
        : String(av ?? '').localeCompare(String(bv ?? ''), 'he', { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [rows, sort]);

  const lastPage = server ? 0 : Math.max(0, Math.ceil(sortedRows.length / pageSize) - 1);
  // Clamp instead of trusting state: when a filter/search shrinks the rows the
  // remembered page can point past the end, rendering an empty table.
  const safePage = Math.min(page, lastPage);
  const pageStart = safePage * pageSize;
  const pageRows = server ? sortedRows : sortedRows.slice(pageStart, pageStart + pageSize);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
        {lang === 'he' ? 'טוען…' : 'Loading…'}
      </div>
    );
  }

  if (!sortedRows.length) {
    if (emptyState) return <>{emptyState}</>;
    return (
      <div className="empty">
        <h4>{lang === 'he' ? 'אין נתונים' : 'No data'}</h4>
        {emptyMessage && <p>{emptyMessage}</p>}
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            // Keeps columns legible on narrow viewports: the table won't shrink
            // below this, so the wrapper's overflow-x scrolls instead of clipping
            // leading columns. Scales with column count.
            minWidth: Math.max(320, columns.length * 110),
            borderCollapse: 'collapse',
            fontSize: 'var(--t-md)',
            textAlign: lang === 'he' ? 'right' : 'left',
          }}
        >
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr style={{ borderBottom: '1px solid var(--line)' }}>
              {selectable && (
                <th style={{ padding: rowPad, width: 32 }}>
                  <input
                    type="checkbox"
                    checked={selected.size === pageRows.length && pageRows.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected(new Set(pageRows.map((_, idx) => pageStart + idx)));
                      } else {
                        setSelected(new Set());
                      }
                    }}
                  />
                </th>
              )}
              {columns.map((col) => {
                const isSorted = sort?.key === col.key;
                const ariaSort = !col.sortable ? undefined : isSorted ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none';
                const label = lang === 'he' ? col.he : col.en ?? col.he;
                const toggle = () =>
                  setSort((cur) =>
                    cur?.key === col.key
                      ? { key: col.key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
                      : { key: col.key, dir: 'asc' }
                  );
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={ariaSort}
                    style={{
                      padding: `${rowPad} var(--pad-card)`,
                      textAlign: col.align ?? (lang === 'he' ? 'right' : 'left'),
                      fontSize: 'var(--t-sm)',
                      color: 'var(--muted)',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      width: col.width,
                      userSelect: 'none',
                    }}
                  >
                    {col.sortable ? (
                      // Real button so keyboard users can sort (WCAG 2.1.1).
                      <button
                        type="button"
                        onClick={toggle}
                        style={{
                          background: 'none', border: 'none', padding: 0, font: 'inherit',
                          color: 'inherit', fontWeight: 500, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        {label}
                        {isSorted && <span aria-hidden="true">{sort!.dir === 'asc' ? '↑' : '↓'}</span>}
                      </button>
                    ) : (
                      col.heNode ?? label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, idx) => {
              const rowIdx = pageStart + idx;
              return (
                <tr
                  key={(row as { id?: number | string }).id ?? rowIdx}
                  onClick={() => onRowClick?.(row)}
                  // Keyboard-operable rows (WCAG 2.1.1): clickable rows become
                  // focusable buttons that fire on Enter/Space.
                  {...(onRowClick
                    ? {
                        tabIndex: 0,
                        role: 'button' as const,
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        },
                      }
                    : {})}
                  // כניסה מדורגת של שורות (עיצוב v2: gcRow, 30ms בין שורות, עד 15).
                  className="anim-row"
                  style={{
                    borderBottom: '1px solid var(--line)',
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'background .2s ease',
                    ['--d' as string]: `${Math.min(idx, 15) * 0.03}s`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--glass-soft)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {selectable && (
                    <td style={{ padding: rowPad }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(rowIdx)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(rowIdx);
                          else next.delete(rowIdx);
                          setSelected(next);
                        }}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{
                        padding: `${rowPad} var(--pad-card)`,
                        textAlign: col.align ?? (lang === 'he' ? 'right' : 'left'),
                      }}
                    >
                      {col.render
                        ? col.render(row)
                        : (() => {
                            const v = (row as Record<string, unknown>)[col.key];
                            return v == null ? '—' : String(v);
                          })()}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(server ? server.lastPage > 1 : lastPage > 0) && (() => {
        // עימוד בסגנון v2: "1–25 מתוך 68" בצד אחד, מספרי עמודים בצד השני.
        const cur = server ? server.page : safePage + 1;
        const last = server ? server.lastPage : lastPage + 1;
        const total = server ? server.total : sortedRows.length;
        const from = server ? (server.page - 1) * pageSize + 1 : pageStart + 1;
        const to = server ? Math.min(from + rows.length - 1, total) : Math.min(pageStart + pageSize, sortedRows.length);
        const go = (n: number) => {
          const t = Math.min(last, Math.max(1, n));
          if (server) server.onPageChange(t); else setPage(t - 1);
        };
        const pages: (number | '…')[] = [];
        for (let n = 1; n <= last; n++) {
          if (n === 1 || n === last || Math.abs(n - cur) <= 2) pages.push(n);
          else if (pages[pages.length - 1] !== '…') pages.push('…');
        }
        const pbtn = (active: boolean) => ({
          minWidth: 32, height: 32, padding: '0 8px', border: 0, borderRadius: 8,
          background: active ? 'var(--text)' : 'transparent', color: active ? 'var(--bg)' : 'var(--text-2)',
          fontWeight: active ? 600 : 500, fontSize: 'var(--t-sm)', cursor: active ? 'default' : 'pointer', fontFamily: 'inherit',
        });
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
              padding: '10px var(--pad-card)',
              fontSize: 'var(--t-sm)',
              color: 'var(--muted)',
            }}
          >
            <span>{lang === 'he' ? `${from}–${to} מתוך ${total}` : `${from}–${to} of ${total}`}</span>
            <nav aria-label={lang === 'he' ? 'עימוד' : 'Pagination'} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button type="button" aria-label={lang === 'he' ? 'הקודם' : 'Prev'} disabled={cur <= 1} onClick={() => go(cur - 1)} style={{ ...pbtn(false), opacity: cur <= 1 ? 0.35 : 1 }}>
                {lang === 'he' ? '›' : '‹'}
              </button>
              {pages.map((n, i) => n === '…'
                ? <span key={`e${i}`} style={{ padding: '0 4px' }}>…</span>
                : <button key={n} type="button" aria-current={n === cur ? 'page' : undefined} onClick={() => go(n)} style={pbtn(n === cur)}>{n}</button>)}
              <button type="button" aria-label={lang === 'he' ? 'הבא' : 'Next'} disabled={cur >= last} onClick={() => go(cur + 1)} style={{ ...pbtn(false), opacity: cur >= last ? 0.35 : 1 }}>
                {lang === 'he' ? '‹' : '›'}
              </button>
            </nav>
          </div>
        );
      })()}
    </div>
  );
}
